import React, {
  type KeyboardEvent,
  type TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./AITextInput.scss";
import { ArrowRightIcon } from "/App.Components";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { createSpreadableBem } from "/Helpers/createBem";

interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  placeholder: string;
  required?: boolean;
  loading: boolean;
  className?: string;
  onEnter?: (value: string) => void;
  prefilledQuestion?: string;
}

const TextInput = (props: Props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const resize = useCallback((textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
  }, []);

  useEffect(() => {
    if (props.prefilledQuestion) {
      setText(props.prefilledQuestion);
      setTimeout(() => {
        const textarea = textAreaRef.current;

        if (textarea) {
          textarea.setAttribute("rows", "1");
          resize(textarea);
        }
      }, 50);
    }
  }, [props.prefilledQuestion]);

  useEffect(() => {
    const textarea = textAreaRef.current;

    if (textarea) {
      textarea.setAttribute("rows", "1");
      resize(textarea);

      textarea.addEventListener("input", () => {
        resize(textarea);
      });
    }
  }, []);

  const onChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
    if (props.onChange) {
      props.onChange(event);
    }
  };

  const sendText = () => {
    if (props.onEnter && text) {
      props.onEnter(text);
      setText(""); // Clear the textarea after sending
      setTimeout(() => {
        if (textAreaRef.current) {
          resize(textAreaRef.current);
        }
      }, 50);
    }
  };

  const onKeyUpHandler = (e: KeyboardEvent) => {
    if (!e.shiftKey && e.key === "Enter") {
      e.preventDefault();
      sendText();
    }
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const bem = createSpreadableBem("ai-text-input");
  return (
    <div {...bem("", { "!AITextInput-focused": isFocused })}>
      <textarea
        ref={textAreaRef}
        tabIndex={0}
        onChange={onChangeHandler}
        placeholder={props.placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (!e.shiftKey && e.key === "Enter") {
            e.preventDefault();
          }
        }}
        onKeyUp={onKeyUpHandler}
        {...bem("__textarea", "!" + (props.className ?? ""))}
        value={text}
        // autoFocus
        data-testid={"chat-input"}
      ></textarea>
      <button {...bem("__button")} onClick={sendText}>
        <ArrowRightIcon size="24" thickness="Reg" color={ColorTheme.black} />
      </button>
    </div>
  );
};
export default TextInput;
